import { useEffect, useState } from "react";
import { getAllOrders } from "../../services/orderApi";
import {
  getAllInvoices,
  createInvoice,
  deleteInvoice,
} from "../../services/invoiceApi";

const InvoiceDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [filters, setFilters] = useState({
    from: "",
    to: "",
  });

  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // ===============================
  // SEARCH (DATE RANGE LOAD)
  // ===============================
  const handleSearch = async () => {
    if (!filters.from || !filters.to) {
      alert("Please select date range");
      return;
    }

    try {
      setLoading(true);

      const [ordersData, invoicesData] = await Promise.all([
        getAllOrders(),
        getAllInvoices(),
      ]);

      // optional filtering by date (frontend safe fallback)
      const from = new Date(filters.from + " 00:00:00");
      const to = new Date(filters.to + " 23:59:59");

      const filteredInvoices = (invoicesData || []).filter((inv) => {
        const d = new Date(inv.invoice_date);
        return d >= from && d <= to;
      });

      setOrders(ordersData || []);
      setInvoices(filteredInvoices || []);

      setSearched(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // CREATE INVOICE
  // ===============================
  const handleCreateInvoice = async (orderId) => {
    try {
      const res = await createInvoice(orderId);

      await handleSearch(); // refresh with current filter

      setSelectedInvoice(res?.invoice || null);
    } catch (err) {
      console.error("Create invoice failed:", err?.response?.data || err);
      alert(err?.response?.data?.message || "Create failed");
    }
  };

  // ===============================
  // DELETE INVOICE
  // ===============================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete invoice?")) return;

    try {
      await deleteInvoice(id);
      handleSearch();
    } catch (err) {
      console.error(err);
    }
  };

  // ===============================
  // PRINT
  // ===============================
  const handlePrint = () => window.print();

  // ===============================
  // PDF
  // ===============================
  const downloadPDF = (id) => {
    window.open(
      `/api/invoices/${id}/pdf`,
      "_blank"
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>🧾 Invoice Dashboard</h2>

      {/* ================= DATE FILTER ================= */}
      <div style={{ marginBottom: "20px" }}>
        <input
          type="date"
          value={filters.from}
          onChange={(e) =>
            setFilters({ ...filters, from: e.target.value })
          }
        />

        <input
          type="date"
          value={filters.to}
          onChange={(e) =>
            setFilters({ ...filters, to: e.target.value })
          }
          style={{ marginLeft: "10px" }}
        />

        <button
          onClick={handleSearch}
          style={{
            marginLeft: "10px",
            padding: "6px 12px",
            background: "#1e293b",
            color: "#fff",
            border: "none",
          }}
        >
          🔍 Search
        </button>
      </div>

      {/* ================= LOADING ================= */}
      {loading && <p>Loading...</p>}

      {/* ================= ORDERS ================= */}
      {searched && (
        <>
          <h3>📦 Orders (Create Invoice)</h3>

          <table border="1" cellPadding="10" width="100%">
            <thead style={{ background: "#e2e8f0" }}>
              <tr>
                <th>DB ID</th>
                <th>Order No</th>
                <th>User</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>{o.id}</td>
                  <td>{o.order_id}</td>
                  <td>{o.user_name}</td>
                  <td>{o.status}</td>

                  <td>
                    <button
                      onClick={() => handleCreateInvoice(o.id)}
                      style={{
                        background: "#1e293b",
                        color: "#fff",
                        padding: "5px 10px",
                        border: "none",
                      }}
                    >
                      Create Invoice
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ================= INVOICES ================= */}
      {searched && (
        <>
          <h3 style={{ marginTop: "30px" }}>🧾 Invoices</h3>

          {invoices.length === 0 ? (
            <p>No invoices found</p>
          ) : (
            <table border="1" cellPadding="10" width="100%">
              <thead style={{ background: "#e2e8f0" }}>
                <tr>
                  <th>ID</th>
                  <th>Invoice No</th>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.id}</td>
                    <td>{inv.invoice_no}</td>
                    <td>{inv.user_name}</td>
                    <td>₹{inv.final_amount}</td>
                    <td>{inv.status}</td>
                    <td>{inv.payment_status}</td>

                    <td>
                      <button onClick={() => setSelectedInvoice(inv)}>
                        Preview
                      </button>

                      <button
                        onClick={() => downloadPDF(inv.id)}
                        style={{ marginLeft: "5px" }}
                      >
                        PDF
                      </button>

                      <button
                        onClick={() => handleDelete(inv.id)}
                        style={{ marginLeft: "5px", color: "red" }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ================= MODAL ================= */}
      {selectedInvoice && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ background: "#fff", padding: 20, width: 600 }}>
            <h3>Invoice Preview</h3>

            <p><b>No:</b> {selectedInvoice.invoice_no}</p>
            <p><b>User:</b> {selectedInvoice.user_name}</p>
            <p><b>Email:</b> {selectedInvoice.user_email}</p>
            <p><b>Amount:</b> ₹{selectedInvoice.final_amount}</p>

            <div style={{ marginTop: 10 }}>
              <button onClick={handlePrint}>Print</button>

              <button
                onClick={() => downloadPDF(selectedInvoice.id)}
                style={{ marginLeft: 10 }}
              >
                PDF
              </button>

              <button
                onClick={() => setSelectedInvoice(null)}
                style={{ marginLeft: 10, color: "red" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDashboard;